import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Router as RouterIcon,
  People as PeopleIcon,
  CreditCard as SubscriptionIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  TrendingUp as TrendingIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/httpClient';
import { connectToGateway, reportGatewayAccessFailure } from '../utils/gatewayAccess';
import SubscriptionPromptDialog from './SubscriptionPromptDialog';

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [stats, setStats] = useState({
    totalGateways: 0,
    onlineGateways: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    trialSubscriptions: 0,
    pendingApprovals: 0,
    monthlyRevenue: 0,
  });
  
  const [recentGateways, setRecentGateways] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingId, setConnectingId] = useState(null);
  const [subscriptionPromptOpen, setSubscriptionPromptOpen] = useState(false);
  const [blockedGateway, setBlockedGateway] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch real data from existing endpoints
      const [gatewaysRes, usersRes, subscriptionsRes, subscriptionStatsRes, pendingApprovalsRes] = await Promise.all([
        api.get('/v1/hub/gateways?showAll=true'),
        api.get('/v1/hub/users'),
        api.get('/v1/admin/subscriptions/active'),
        api.get('/v1/admin/subscriptions/stats'),
        api.get('/v1/admin/gateway-requests/pending'),
      ]);

      const gateways = gatewaysRes.data || [];
      const users = usersRes.data || [];
      const subscriptions = subscriptionsRes.data || [];
      const subStats = subscriptionStatsRes.data || {};
      const pendingApprovals = pendingApprovalsRes.data || [];

      // Calculate statistics
      const onlineGateways = gateways.filter(g => g.status === 'online').length;
      const activeUsers = users.filter(u => u.active).length;
      const trialSubs = subscriptions.filter(s => s.status === 'TRIAL').length;
      
      setStats({
        totalGateways: gateways.length,
        onlineGateways: onlineGateways,
        totalUsers: users.length,
        activeUsers: activeUsers,
        totalSubscriptions: subStats.totalSubscriptions || 0,
        activeSubscriptions: subStats.activeCount || 0,
        trialSubscriptions: trialSubs,
        pendingApprovals: pendingApprovals.length,
        monthlyRevenue: subStats.monthlyRevenue || 0,
      });

      // Get recent gateways (last 5, sorted by lastSeen)
      const sortedGateways = [...gateways]
        .sort((a, b) => new Date(b.lastSeen || 0) - new Date(a.lastSeen || 0))
        .slice(0, 5);
      setRecentGateways(sortedGateways);

      // Get recent users (last 5, sorted by createdAt)
      const sortedUsers = [...users]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5);
      setRecentUsers(sortedUsers);

      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Some information may be unavailable.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (gateway) => {
    setConnectingId(gateway.id);
    try {
      const result = await connectToGateway(gateway);
      if (result.reason === 'subscription_required') {
        setBlockedGateway(gateway);
        setSubscriptionPromptOpen(true);
        return;
      }
      reportGatewayAccessFailure(result);
    } finally {
      setConnectingId(null);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Admin Dashboard
        </Typography>
        <Button
          variant="outlined"
          onClick={fetchDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Gateways */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/gateways')}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Gateways
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalGateways}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {stats.onlineGateways} online
                  </Typography>
                </Box>
                <RouterIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/users')}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Users
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {stats.activeUsers} active
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Subscriptions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/subscriptions')}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Subscriptions
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalSubscriptions}
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    {stats.trialSubscriptions} in trial
                  </Typography>
                </Box>
                <SubscriptionIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Monthly Revenue
                  </Typography>
                  <Typography variant="h4">
                    ${stats.monthlyRevenue.toFixed(0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.activeSubscriptions} active
                  </Typography>
                </Box>
                <TrendingIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Actions */}
      {stats.pendingApprovals > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/gateway-approvals')}>
              Review
            </Button>
          }
        >
          <Typography variant="body1" fontWeight="bold">
            {stats.pendingApprovals} gateway registration{stats.pendingApprovals > 1 ? 's' : ''} pending approval
          </Typography>
          <Typography variant="body2">
            New gateway registrations require admin approval before they can connect.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Recent Gateways */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Recent Gateways
              </Typography>
              <Button size="small" onClick={() => navigate('/gateways')}>
                View All
              </Button>
            </Box>
            <List>
              {recentGateways.length === 0 ? (
                <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                  No gateways registered yet
                </Typography>
              ) : (
                recentGateways.map((gateway) => (
                  <ListItem 
                    key={gateway.id} 
                    sx={{ 
                      px: 0,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <ListItemIcon>
                      {gateway.status === 'online' ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={gateway.name || gateway.deviceName}
                      secondary={
                        <>
                          {gateway.location || gateway.deviceLocation || 'No location'} • 
                          {gateway.owner ? ` ${gateway.owner.username}` : ' Unclaimed'} • 
                          Last seen: {formatTimestamp(gateway.lastSeen)}
                        </>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1, flexShrink: 0 }}>
                      <Chip
                        label={gateway.status}
                        size="small"
                        color={gateway.status === 'online' ? 'success' : 'default'}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<LaunchIcon />}
                        onClick={() => handleConnect(gateway)}
                        disabled={connectingId === gateway.id}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Connect
                      </Button>
                    </Box>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* Recent Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Recent Users
              </Typography>
              <Button size="small" onClick={() => navigate('/users')}>
                View All
              </Button>
            </Box>
            <List>
              {recentUsers.length === 0 ? (
                <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                  No users registered yet
                </Typography>
              ) : (
                recentUsers.map((user) => (
                  <ListItem 
                    key={user.id} 
                    sx={{ 
                      px: 0,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <ListItemIcon>
                      {user.isGatewayOwner ? (
                        <CheckIcon color="error" />
                      ) : (
                        <PeopleIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.username
                          }
                          {user.isGatewayOwner && (
                            <Chip label="Admin" size="small" color="error" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          {user.email} • 
                          {user.gatewayCount > 0 ? ` ${user.gatewayCount} gateway${user.gatewayCount > 1 ? 's' : ''}` : ' No gateways'} • 
                          Joined: {formatTimestamp(user.createdAt)}
                        </>
                      }
                    />
                    <Chip 
                      label={user.active ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={user.active ? 'success' : 'default'}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <SubscriptionPromptDialog
        open={subscriptionPromptOpen}
        gatewayName={blockedGateway?.name || blockedGateway?.deviceName}
        onClose={() => setSubscriptionPromptOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;
